/**
 * ⟦descarga-con-estado⟧ `R4-UI0` · **LA DESCARGA QUE SÍ PUEDE FALLAR Y DECIRLO.**
 *
 * A6 de `R4_decisiones_de_ejecucion.md`: la descarga es UNA primitiva de
 * `ui-marco` y no una copia por pantalla. Hasta R4 las tres descargas de la
 * consola NAVEGABAN (`window.location.assign` en `conversaciones`,
 * `metricas` y `exportacion`): el navegador bajaba el archivo por su cuenta, el
 * JavaScript no se enteraba de si el servidor había contestado un 500 ni podía
 * mostrar «Descargando…» mientras el CSV se armaba. Esta función cierra esa
 * salida cómoda.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ EL NOMBRE SALE SIEMPRE DEL SERVIDOR. NUNCA HAY UN NOMBRE DE RESPALDO.
 * ════════════════════════════════════════════════════════════════════════════
 *
 * La salida cómoda que A6 (b) prohíbe es `a.download = "export.csv"` «por si la
 * cabecera no llega»: la consola elegiría un nombre que no tiene ni la instancia
 * ni el rango, y un `Expose-Headers` borrado en `admin-web` pasaría todos los
 * exámenes de pantalla bajando archivos mal nombrados. Acá, si
 * `Content-Disposition` falta o no se puede leer, **se lanza**: la descarga va
 * por el camino de error y la pantalla muestra su aviso (`onError`), nunca un
 * archivo con un nombre inventado.
 *
 * ⚠️ **Por qué `fetch` y no `<a href>`:** la consola habla con `admin-web` desde
 * otro origen, así que el pedido necesita `credentials: "include"` para llevar
 * la cookie `HttpOnly` de la sesión. Un `<a download>` sin `fetch` visita la URL
 * sin credenciales y el servidor contesta el login en vez del CSV.
 */

/** El nombre de la cabecera que trae el nombre del archivo (literal del HTTP). */
const CABECERA_NOMBRE = "Content-Disposition"

/**
 * `filename=...` de un `Content-Disposition`, con o sin comillas. Es la forma
 * que sirve `admin-web`; cualquier otra cosa se trata como «no llegó el nombre».
 */
const NOMBRE_EN_LA_CABECERA = /filename="?([^";]+)"?/

/**
 * Baja el archivo que sirve `url` y lo entrega al navegador por `<a download>`.
 *
 * @throws si el servidor no responde `ok`, o si la respuesta no trae un nombre
 * de archivo legible en `Content-Disposition`.
 */
export async function descargarArchivo(url: string): Promise<void> {
  const respuesta = await fetch(url, { credentials: "include" })

  /*
   * ⛔ Un 500 o un 404 con `blob()` bajaría un Blob de error con extensión `.csv`
   * en vez de ir por el camino de error. El estado se mira ANTES de leer el
   * cuerpo.
   */
  if (!respuesta.ok) throw new Error(`No se pudo descargar el archivo (${respuesta.status}).`)

  const disposicion = respuesta.headers.get(CABECERA_NOMBRE) ?? ""
  const coincidencia = NOMBRE_EN_LA_CABECERA.exec(disposicion)
  if (coincidencia === null) {
    throw new Error("El servidor no dijo el nombre del archivo (sin Content-Disposition).")
  }

  const blob = await respuesta.blob()
  const objectUrl = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = objectUrl
  a.download = coincidencia[1]!
  a.click()
  /*
   * Libera el objectURL: sin esto el Blob entero (el CSV completo, que A6 ya
   * anota que pasa por la memoria) queda retenido hasta que se recargue la SPA.
   */
  URL.revokeObjectURL(objectUrl)
}
